import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Layout } from '../components/layout/Layout';
import { ProductCard } from '../components/ProductCard';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { ArrowRight, Truck, RotateCcw, Shield, Sparkles } from 'lucide-react';
import { motion } from 'framer-motion';

const heroImages = [
  'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=2000&auto=format&fit=crop',
  'https://images.unsplash.com/photo-1766928210443-0be92ed5884a?q=80&w=2000&auto=format&fit=crop',
];

export const HomePage = () => {
  const { t, getLocalizedText, categories, API, language } = useApp();
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [saleProducts, setSaleProducts] = useState([]);
  const [sliders, setSliders] = useState([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [featuredRes, saleRes, slidersRes] = await Promise.all([
          fetch(`${API}/products?featured=true&limit=8`),
          fetch(`${API}/products?on_sale=true&limit=4`),
          fetch(`${API}/sliders`),
        ]);

        const featuredData = await featuredRes.json();
        const saleData = await saleRes.json();
        const slidersData = await slidersRes.json();

        setFeaturedProducts(featuredData.products || []);
        setSaleProducts(saleData.products || []);
        setSliders(slidersData || []);
      } catch (err) {
        console.error('Failed to fetch home data', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [API]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % Math.max(sliders.length, heroImages.length));
    }, 5000);
    return () => clearInterval(interval);
  }, [sliders.length]);

  const features = [
    {
      icon: () => (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          <path d="M9 12l2 2 4-4"/>
        </svg>
      ),
      title: language === 'ro' ? 'Un brand construit pe transparență' : 'A brand built on transparency',
      desc: language === 'ro' ? 'Servicii orientate spre client' : 'Customer-oriented services'
    },
    { 
      icon: Truck, 
      title: language === 'ro' ? 'Livrare Gratuită' : 'Free Shipping', 
      desc: language === 'ro' ? 'La comenzi de minim 349 lei' : 'For orders over 349 lei' 
    },
    { 
      icon: () => (
        <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
        </svg>
      ), 
      title: language === 'ro' ? 'Asistență telefonică' : 'Phone Support', 
      desc: '0732901128' 
    },
    { 
      icon: Shield, 
      title: language === 'ro' ? 'Plăți sigure' : 'Secure Payments', 
      desc: language === 'ro' ? 'Card sau ramburs' : 'Card or cash on delivery' 
    },
  ];

  const categoryImages = {
    'bed-linen': 'https://images.unsplash.com/photo-1763478959550-13d564c5a62d?q=80&w=800&auto=format&fit=crop',
    'blankets-pillows': 'https://images.unsplash.com/photo-1760067538263-fb7126fe748c?q=80&w=800&auto=format&fit=crop',
    'kitchen-bathroom': 'https://images.pexels.com/photos/45980/pexels-photo-45980.jpeg?auto=compress&cs=tinysrgb&h=650&w=940',
    'furniture-covers': 'https://images.unsplash.com/photo-1760822604131-3bded72f7caf?q=80&w=800&auto=format&fit=crop',
  };

  return (
    <Layout>
      <SEO
        title={language === 'ro' ? 'Lenjerii de Pat Premium' : 'Premium Bed Linens'}
        description={language === 'ro' 
          ? 'Rani Store - Magazin online de lenjerii de pat premium. Livrare gratuită peste 349 lei. Calitate garantată, retur simplu în 14 zile.'
          : 'Rani Store - Online store for premium bed linens. Free delivery over 349 lei. Guaranteed quality, easy returns within 14 days.'}
        keywords={language === 'ro'
          ? 'lenjerii de pat, lenjerie pat, cearceafuri, huse pilota, fete perna, bumbac, satin, percale, magazin online'
          : 'bed linens, bedsheets, pillow cases, duvet covers, cotton, satin, percale, online store'}
        url="/"
        language={language}
      />
      
      {/* Hero Section */}
      <section className="relative h-[70vh] sm:h-[80vh] overflow-hidden bg-gradient-to-br from-white via-purple-50/30 to-purple-100/20" data-testid="hero-section">
        <div className="absolute inset-0">
          {(sliders.length > 0 ? sliders : heroImages.map((img, i) => ({ image: img, id: i, link: '/shop' }))).map((slide, index) => (
            <Link 
              key={slide.id || index}
              to={slide.link || '/shop'}
              className={`absolute inset-0 transition-opacity duration-1000 cursor-pointer ${
                index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
            >
              <img
                src={slide.image || heroImages[index % heroImages.length]}
                alt=""
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-white/90 via-white/70 to-transparent" />
            </Link>
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 h-full flex items-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-xl"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4 font-medium">
              {t('collections')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1A1625] leading-tight mb-6">
              {sliders[currentSlide]?.title
                ? getLocalizedText(sliders[currentSlide].title)
                : t('heroTitle')}
            </h1>
            <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
              {sliders[currentSlide]?.subtitle
                ? getLocalizedText(sliders[currentSlide].subtitle)
                : t('heroSubtitle')}
            </p>
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="bg-primary hover:bg-purple-600 text-white rounded-full px-8 h-12 shadow-lg shadow-primary/25"
                data-testid="hero-shop-btn"
              >
                <Link to="/shop">
                  {t('shopNow')} <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-2 border-primary/20 text-primary hover:bg-primary/5 rounded-full px-8 h-12"
                data-testid="hero-collections-btn"
              >
                <Link to="/shop?category=collections">
                  {t('discoverCollection')}
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Slide indicators */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex gap-2">
          {(sliders.length > 0 ? sliders : heroImages).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide ? 'bg-primary w-8' : 'bg-gray-300'
              }`}
              aria-label={`Slide ${index + 1}`}
            />
          ))}
        </div>
      </section>

      {/* Features */}
      <section className="py-12 border-b border-gray-100 bg-gradient-to-b from-white to-purple-50/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex flex-col items-center text-center p-4 md:p-6 bg-white rounded-xl shadow-sm border border-gray-100"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 rounded-full bg-purple-50 flex items-center justify-center mb-3 md:mb-4">
                  <feature.icon className="h-5 w-5 md:h-6 md:w-6 text-primary" />
                </div>
                <h3 className="font-semibold text-foreground mb-1 text-sm md:text-base">{feature.title}</h3>
                <p className="text-xs md:text-sm text-muted-foreground">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Bento Grid */}
      <section className="py-16 sm:py-24" data-testid="categories-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="text-center mb-12">
            <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4">{t('shop')}</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1625]">
              {t('categories')}
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {categories.slice(0, 8).map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                viewport={{ once: true }}
                className={`relative overflow-hidden rounded-xl group cursor-pointer ${
                  index === 0 || index === 3 ? 'md:col-span-2 md:row-span-2' : ''
                }`}
              >
                <Link to={`/shop?category=${category.id}`}>
                  <div className={`${index === 0 || index === 3 ? 'aspect-square' : 'aspect-[4/3]'}`}>
                    <img
                      src={category.image || categoryImages[category.slug] || heroImages[index % 2]}
                      alt={getLocalizedText(category.name)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <h3 className="font-heading text-lg sm:text-xl font-semibold text-white">
                        {getLocalizedText(category.name)}
                      </h3>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      {featuredProducts.length > 0 && (
        <section className="py-16 sm:py-24 bg-surface" data-testid="featured-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4">{t('newArrivals')}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1625]">
                  {t('featured')}
                </h2>
              </div>
              <Button asChild variant="ghost" className="text-primary hover:text-gold">
                <Link to="/shop">
                  {t('shop')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {featuredProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sale Products */}
      {saleProducts.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="sale-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
            <div className="flex justify-between items-end mb-12">
              <div>
                <p className="text-sm tracking-[0.3em] uppercase text-red-500 mb-4">{t('onSale')}</p>
                <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#1A1625]">
                  {t('onSale')}
                </h2>
              </div>
              <Button asChild variant="ghost" className="text-primary hover:text-gold">
                <Link to="/shop?on_sale=true">
                  {t('shop')} <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {saleProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Newsletter / CTA */}
      <section className="py-16 sm:py-24 bg-[#1A1625]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.3em] uppercase text-gold mb-4">Rani Store</p>
            <h2 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-6">
              {t('heroTitle')}
            </h2>
            <p className="text-gray-400 mb-8 max-w-2xl mx-auto">
              {t('heroSubtitle')}
            </p>
            <Button
              asChild
              size="lg"
              className="bg-gold hover:bg-yellow-600 text-white rounded-full px-8 h-12"
            >
              <Link to="/shop">{t('shopNow')}</Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
};

export default HomePage;
